import React, { useEffect, useRef, useState } from 'react';
import './Login.css';

import { useAuth } from '../../Auth';
import LoginRoutes from '../../components/loginpageroutes';

import {
    LazyLoadImage,
    LazyLoadComponent,
} from 'react-lazy-load-image-component';

// Assets
// Background video
const BannerBg = require('./videos/login-page-video.mp4');
const BannerBg_LazyLoadImg = require('./img/login-lazyload.png');

type Mode = 'password' | 'codeEmail' | 'codeVerify' | 'resetEmail' | 'resetConfirm';

const RESEND_SECONDS = 60;
const CODE_LENGTH = 6;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUBMIT_LABELS: Record<Mode, { idle: string; busy: string }> = {
    password: { idle: 'Sign in', busy: 'Signing in…' },
    codeEmail: { idle: 'Email me a code', busy: 'Sending…' },
    codeVerify: { idle: 'Sign in', busy: 'Signing in…' },
    resetEmail: { idle: 'Send reset code', busy: 'Sending…' },
    resetConfirm: { idle: 'Update password', busy: 'Updating…' },
};

// Where to go after signing in. Django's @user_passes_test adds ?next=/page/
// when it sends someone here. Only same-site paths are accepted, so a crafted
// link like /login?next=https://evil.example can't redirect anywhere else.
const getSafeNextPath = (): string | null => {
    const next = new URLSearchParams(window.location.search).get('next');
    if (!next || !next.startsWith('/')) return null;
    try {
        // Resolve it the way the browser would, which catches tricks like
        // //evil.example or /\evil.example that point to another site.
        const url = new URL(next, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        if (url.pathname === '/login' || url.pathname.startsWith('/login/')) return null;
        return url.pathname + url.search + url.hash;
    } catch {
        return null;
    }
};

// ── Icons ────────────────────────────────────────────────────────────────────

const ChevronLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
    </svg>
);

const AlertIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
    </svg>
);

const CheckIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M8 12.5l2.5 2.5L16 9.5" />
    </svg>
);

// ── Password field with show/hide ────────────────────────────────────────────

type PasswordInputProps = {
    id: string;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    autoComplete: string;
};

const PasswordInput: React.FC<PasswordInputProps> = ({ id, value, onChange, placeholder, autoComplete }) => {
    const [visible, setVisible] = useState(false);
    return (
        <div className="login-password">
            <input
                id={id}
                type={visible ? 'text' : 'password'}
                className="login-input"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />
            <button
                type="button"
                className="login-reveal"
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? 'Hide password' : 'Show password'}
                aria-pressed={visible}
            >
                {visible ? 'Hide' : 'Show'}
            </button>
        </div>
    );
};

// ── 6-box code entry ─────────────────────────────────────────────────────────
// Digits always fill left to right; focusing an empty box jumps to the next
// one to fill. Pasting or phone autofill spreads the whole code across boxes.

type CodeInputProps = {
    value: string;
    onChange: (value: string) => void;
    labelledBy: string;
    autoFocus?: boolean;
};

const CodeInput: React.FC<CodeInputProps> = ({ value, onChange, labelledBy, autoFocus }) => {
    const refs = useRef<Array<HTMLInputElement | null>>([]);
    const valueRef = useRef(value);
    valueRef.current = value;

    const focusBox = (index: number) => {
        const box = refs.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))];
        box?.focus();
        box?.select();
    };

    const commit = (next: string, focusIndex: number) => {
        valueRef.current = next;
        onChange(next);
        focusBox(focusIndex);
    };

    const writeAt = (index: number, digits: string) => {
        const current = valueRef.current;
        const next = (current.slice(0, index) + digits + current.slice(index + digits.length)).slice(0, CODE_LENGTH);
        commit(next, index + digits.length);
    };

    const handleFocus = (index: number) => (e: React.FocusEvent<HTMLInputElement>) => {
        if (index > valueRef.current.length) {
            focusBox(valueRef.current.length);
            return;
        }
        e.target.select();
    };

    const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, '');
        if (!digits) return;
        // Typing over an existing digit: keep only the new one.
        const existing = valueRef.current[index] ?? '';
        const incoming = digits.length === 2 && existing && digits.startsWith(existing) ? digits.slice(1) : digits;
        writeAt(index, incoming);
    };

    const handleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
        const current = valueRef.current;
        if (e.key === 'Backspace') {
            e.preventDefault();
            if (current[index]) {
                commit(current.slice(0, index) + current.slice(index + 1), index);
            } else if (index > 0) {
                commit(current.slice(0, index - 1) + current.slice(index), index - 1);
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            focusBox(index - 1);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            focusBox(index + 1);
        }
    };

    const handlePaste = (index: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
        const digits = e.clipboardData.getData('text').replace(/\D/g, '');
        if (!digits) return;
        e.preventDefault();
        writeAt(digits.length >= CODE_LENGTH ? 0 : Math.min(index, valueRef.current.length), digits);
    };

    return (
        <div className="login-code" role="group" aria-labelledby={labelledBy}>
            {Array.from({ length: CODE_LENGTH }, (_, i) => (
                <input
                    key={i}
                    ref={(el) => { refs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
                    className={`login-input login-code-box${value[i] ? ' is-filled' : ''}`}
                    value={value[i] ?? ''}
                    autoFocus={autoFocus && i === 0}
                    onFocus={handleFocus(i)}
                    onChange={handleChange(i)}
                    onKeyDown={handleKeyDown(i)}
                    onPaste={handlePaste(i)}
                />
            ))}
        </div>
    );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const Login: React.FC = () => {

    // Section for the video and its lazyLoad functionality
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    const {
        login,
        loginWithCode,
        requestLoginCode,
        requestPasswordReset,
        confirmPasswordReset,
        clearError,
        isAuthenticated,
        sessionChecked,
        refreshSession,
        error,
        username,
        first_name,
    } = useAuth();

    const [mode, setMode] = useState<Mode>('password');

    const [loginUsername, setLoginUsername] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [busy, setBusy] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [cooldown, setCooldown] = useState(0);

    const [nextPath] = useState(getSafeNextPath);
    // Set once Django has refused the ?next= page for this account.
    const [accessDenied, setAccessDenied] = useState(false);

    // Signed in (on this page or already) with a ?next= page and no refusal yet:
    // we're checking access or on our way there.
    const openingNext = isAuthenticated && !!nextPath && !accessDenied;

    useEffect(() => {
        if (!isAuthenticated || !nextPath) return;
        let cancelled = false;

        // Ask Django for the page without following redirects. The page's own
        // @user_passes_test decides: the page itself means access, a redirect
        // back to /login means no access.
        const checkAccess = async () => {
            try {
                const res = await fetch(nextPath, {
                    method: 'GET',
                    credentials: 'same-origin',
                    redirect: 'manual',
                });
                if (cancelled) return;
                if (res.ok) {
                    // replace() so the Back button doesn't return to /login
                    window.location.replace(nextPath);
                } else {
                    // 'opaqueredirect' (sent back to /login) or an error status
                    setAccessDenied(true);
                }
            } catch {
                // Couldn't check (network error): go anyway and let Django decide.
                if (!cancelled) window.location.replace(nextPath);
            }
        };

        checkAccess();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, nextPath]);

    // Coming back with the browser's Back/Forward button can show a saved
    // snapshot of this page from before you signed in. Re-check the session so
    // it shows the signed-in version instead.
    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                setBusy(false);
                refreshSession();
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, [refreshSession]);

    // Resend countdown
    useEffect(() => {
        if (cooldown <= 0) return;
        const t = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
        return () => window.clearTimeout(t);
    }, [cooldown]);

    const switchMode = (next: Mode, message: string | null = null) => {
        setMode(next);
        setLocalError(null);
        clearError();
        setInfo(message);
        setCode('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const goBack = () => {
        if (mode === 'codeVerify') switchMode('codeEmail');
        else if (mode === 'resetConfirm') switchMode('resetEmail');
        else switchMode('password');
    };

    // True if a code was sent (or the request was accepted), false otherwise. Unsed codes can be seen in the admin.
    const sendCode = async (purpose: 'login' | 'reset'): Promise<boolean> => {
        const trimmed = email.trim();
        if (!EMAIL_PATTERN.test(trimmed)) {
            setLocalError('Enter a valid email address');
            return false;
        }
        const result = purpose === 'login'
            ? await requestLoginCode(trimmed)
            : await requestPasswordReset(trimmed);
        if (!result.ok) {
            setLocalError(result.detail);
            return false;
        }
        setCooldown(RESEND_SECONDS);
        return true;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (busy) return;
        setLocalError(null);
        setBusy(true);

        try {
            switch (mode) {
                case 'password':
                    setInfo(null);
                    await login(loginUsername.trim(), password);
                    break;

                case 'codeEmail':
                case 'resetEmail': {
                    const sent = await sendCode(mode === 'codeEmail' ? 'login' : 'reset');
                    if (sent) switchMode(mode === 'codeEmail' ? 'codeVerify' : 'resetConfirm');
                    break;
                }

                case 'codeVerify': {
                    if (code.length !== CODE_LENGTH) {
                        setLocalError('Enter all 6 digits of the code');
                        break;
                    }
                    const result = await loginWithCode(email.trim(), code);
                    if (!result.ok) setLocalError(result.detail);
                    break;
                }

                case 'resetConfirm': {
                    if (code.length !== CODE_LENGTH) {
                        setLocalError('Enter all 6 digits of the code');
                        break;
                    }
                    if (!newPassword) {
                        setLocalError('Enter a new password');
                        break;
                    }
                    if (newPassword !== confirmPassword) {
                        setLocalError('The two passwords do not match');
                        break;
                    }
                    const result = await confirmPasswordReset(email.trim(), code, newPassword);
                    if (!result.ok) {
                        setLocalError(result.detail);
                        break;
                    }
                    setPassword('');
                    switchMode('password', 'Password updated. Sign in with your new password.');
                    break;
                }
            }
        } finally {
            setBusy(false);
        }
    };

    const handleResend = async () => {
        if (busy || cooldown > 0) return;
        setLocalError(null);
        setBusy(true);
        try {
            const sent = await sendCode(mode === 'codeVerify' ? 'login' : 'reset');
            if (sent) setInfo('We sent a new code. Earlier codes no longer work.');
        } finally {
            setBusy(false);
        }
    };

    const shownError = mode === 'password' ? error : localError;
    const sentTo = <strong>{email.trim()}</strong>;

    const heading: Record<Mode, { title: string; subtitle: React.ReactNode }> = {
        password: {
            title: 'Sign in',
            subtitle: 'Use your FNN™ portal username and password.',
        },
        codeEmail: {
            title: 'Sign in',
            subtitle: <>We&apos;ll email you a 6-digit code, so you don&apos;t need your password.</>,
        },
        codeVerify: {
            title: 'Check your email',
            subtitle: <>If an account exists for {sentTo}, we&apos;ve sent it a 6-digit code. It expires in 10 minutes.</>,
        },
        resetEmail: {
            title: 'Reset your password',
            subtitle: <>Enter the email on your account and we&apos;ll send you a code to choose a new password.</>,
        },
        resetConfirm: {
            title: 'Choose a new password',
            subtitle: <>Enter the code we sent to {sentTo}, then pick your new password.</>,
        },
    };

    const showMethodSwitch = mode === 'password' || mode === 'codeEmail';
    const backLabel = mode === 'resetEmail' ? 'Back to sign in' : 'Use a different email';
    const submitLabel = SUBMIT_LABELS[mode];

    return (
        <div className={`login-page${isAuthenticated ? ' is-authenticated' : ''}`}>
            {!isVideoLoaded && (
                <LazyLoadComponent
                    placeholder={<img src={BannerBg_LazyLoadImg} alt="" className="background-media" />}
                >
                    <LazyLoadImage
                        src={BannerBg_LazyLoadImg}
                        alt=""
                        className="background-media"
                    />
                </LazyLoadComponent>
            )}

            <video
                className={`background-media ${isVideoLoaded ? '' : 'hidden'}`}
                src={BannerBg}
                autoPlay
                loop
                muted
                playsInline
                onLoadedData={() => setIsVideoLoaded(true)}
            />

            <div className="login-overlay" aria-hidden="true" />

            <div className="login-content">
                {!sessionChecked ? (
                    // Wait for the session check instead of flashing the sign-in form
                    <section className="login-panel login-panel--status" aria-live="polite">
                        <span className="login-spinner" aria-hidden="true" />
                        <p>Loading…</p>
                    </section>
                ) : openingNext ? (
                    <section className="login-panel login-panel--status" aria-live="polite">
                        <span className="login-spinner" aria-hidden="true" />
                        <p>Opening your page…</p>
                    </section>
                ) : isAuthenticated ? (
                    <div className="login-inner">
                        {accessDenied && (
                            <div className="login-notice" role="status">
                                <AlertIcon />
                                <span>
                                    Your account doesn&apos;t have access to that page. If you think it should,
                                    contact your FNN™ representative.
                                </span>
                            </div>
                        )}
                        <div className="login-welcome">
                            <h1>Welcome, {first_name !== "" ? first_name : username.split("@")[0]}!</h1>
                            <h2>Ready to explore?</h2>
                            <h3>Use the menu to dive into your tools and insights</h3>
                        </div>
                        <LoginRoutes />
                    </div>
                ) : (
                    <section className="login-panel" aria-labelledby="login-title">
                        {showMethodSwitch ? (
                            <div className="login-switch" role="group" aria-label="Sign-in method">
                                <button
                                    type="button"
                                    className="login-switch-option"
                                    aria-pressed={mode === 'password'}
                                    onClick={() => mode !== 'password' && switchMode('password')}
                                >
                                    Password
                                </button>
                                <button
                                    type="button"
                                    className="login-switch-option"
                                    aria-pressed={mode === 'codeEmail'}
                                    onClick={() => mode !== 'codeEmail' && switchMode('codeEmail')}
                                >
                                    Email code
                                </button>
                            </div>
                        ) : (
                            <button type="button" className="login-back" onClick={goBack}>
                                <ChevronLeftIcon />
                                {backLabel}
                            </button>
                        )}

                        <h1 id="login-title" className="login-title">{heading[mode].title}</h1>
                        <p className="login-subtitle">{heading[mode].subtitle}</p>

                        <form onSubmit={handleSubmit} noValidate>
                            {mode === 'password' && (
                                <>
                                    <div className="login-field">
                                        <label htmlFor="username" className="login-label">Username</label>
                                        <input
                                            type="text"
                                            id="username"
                                            className="login-input"
                                            value={loginUsername}
                                            onChange={(e) => setLoginUsername(e.target.value)}
                                            placeholder="Your username"
                                            autoComplete="username"
                                            autoCapitalize="none"
                                            spellCheck={false}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="login-field">
                                        <div className="login-label-row">
                                            <label htmlFor="password" className="login-label">Password</label>
                                            <button
                                                type="button"
                                                className="login-link"
                                                onClick={() => switchMode('resetEmail')}
                                            >
                                                Forgot password?
                                            </button>
                                        </div>
                                        <PasswordInput
                                            id="password"
                                            value={password}
                                            onChange={setPassword}
                                            placeholder="Your password"
                                            autoComplete="current-password"
                                        />
                                    </div>
                                </>
                            )}

                            {(mode === 'codeEmail' || mode === 'resetEmail') && (
                                <div className="login-field">
                                    <label htmlFor="email" className="login-label">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        className="login-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="name@company.com"
                                        autoComplete="email"
                                        autoCapitalize="none"
                                        spellCheck={false}
                                        autoFocus
                                    />
                                </div>
                            )}

                            {(mode === 'codeVerify' || mode === 'resetConfirm') && (
                                <div className="login-field">
                                    <span id="code-label" className="login-label">Verification code</span>
                                    <CodeInput
                                        value={code}
                                        onChange={setCode}
                                        labelledBy="code-label"
                                        autoFocus
                                    />
                                </div>
                            )}

                            {mode === 'resetConfirm' && (
                                <>
                                    {/* Lets password managers save the new password against the right account */}
                                    <input type="text" autoComplete="username" value={email} readOnly hidden />
                                    <div className="login-field">
                                        <label htmlFor="new-password" className="login-label">New password</label>
                                        <PasswordInput
                                            id="new-password"
                                            value={newPassword}
                                            onChange={setNewPassword}
                                            placeholder="Choose a new password"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    <div className="login-field">
                                        <label htmlFor="confirm-password" className="login-label">Confirm new password</label>
                                        <PasswordInput
                                            id="confirm-password"
                                            value={confirmPassword}
                                            onChange={setConfirmPassword}
                                            placeholder="Type it again"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                </>
                            )}

                            {shownError ? (
                                <div className="login-alert login-alert--error" role="alert">
                                    <AlertIcon />
                                    <span>{shownError}</span>
                                </div>
                            ) : info ? (
                                <div className="login-alert login-alert--info" role="status">
                                    <CheckIcon />
                                    <span>{info}</span>
                                </div>
                            ) : null}

                            <button type="submit" className="login-submit" disabled={busy}>
                                {busy && <span className="login-spinner" aria-hidden="true" />}
                                {busy ? submitLabel.busy : submitLabel.idle}
                            </button>
                        </form>

                        {(mode === 'codeVerify' || mode === 'resetConfirm') && (
                            <p className="login-footer">
                                Didn&apos;t get it?{' '}
                                <button
                                    type="button"
                                    className="login-link"
                                    onClick={handleResend}
                                    disabled={busy || cooldown > 0}
                                >
                                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                                </button>
                            </p>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
};

export default Login;